import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

type TimeRange = {
  from: string | null;
  to: string | null;
};

export type TimeRangePickerProps = {
  value: TimeRange;
  onChange: (value: TimeRange) => void;
  label?: string;
  helperText?: string;
  direction?: 'row' | 'column';
  disabled?: boolean;
};

const TIME_FORMAT = 'HH:mm';

export const TimeRangePicker = (props: TimeRangePickerProps) => {
  const { value, onChange, label, helperText, direction = 'row', disabled } = props;

  const fromDayjs = value.from ? dayjs(value.from, TIME_FORMAT) : null;
  const toDayjs = value.to ? dayjs(value.to, TIME_FORMAT) : null;

  const handleFromChange = (from: Dayjs | null) => {
    onChange({ ...value, from: from ? from.format(TIME_FORMAT) : null });
  };

  const handleToChange = (to: Dayjs | null) => {
    onChange({ ...value, to: to ? to.format(TIME_FORMAT) : null });
  };

  return (
    <Box width="100%">
      {label && (
        <Typography variant="body2" mb={1} color="textPrimary" fontWeight={400}>
          {label}
        </Typography>
      )}
      <Stack
        direction={direction}
        spacing={1}
        alignItems={direction === 'row' ? 'center' : 'stretch'}
      >
        <TimePicker
          value={fromDayjs}
          onChange={handleFromChange}
          disabled={disabled}
          ampm={false}
          slotProps={{
            textField: { size: 'small', fullWidth: true, variant: 'outlined' },
          }}
          localeText={{
            fieldHoursPlaceholder: () => '20',
            fieldMinutesPlaceholder: () => '00',
          }}
        />
        {direction === 'row' && <Typography>-</Typography>}
        <TimePicker
          value={toDayjs}
          onChange={handleToChange}
          disabled={disabled}
          ampm={false}
          slotProps={{
            textField: { size: 'small', fullWidth: true, variant: 'outlined' },
          }}
          localeText={{
            fieldHoursPlaceholder: () => '21',
            fieldMinutesPlaceholder: () => '00',
          }}
        />
      </Stack>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};
